import { z } from 'zod';

/** Order matters: the service runs them in this order and Retry resumes from the failed one. */
export const WHATSAPP_ONBOARDING_STEPS = [
  'preflight',
  'add_number',
  'request_code',
  'verify_code',
  'register',
  'enable_calling',
  'subscribe',
  'sync',
] as const;

export const whatsappOnboardingStepSchema = z.enum(WHATSAPP_ONBOARDING_STEPS);
export type WhatsAppOnboardingStep = z.infer<typeof whatsappOnboardingStepSchema>;

export const whatsappOnboardingStatusSchema = z.enum([
  'pending',
  'running',
  'waiting_code',
  'failed',
  'done',
]);
export type WhatsAppOnboardingStatus = z.infer<typeof whatsappOnboardingStatusSchema>;

export const whatsappOnboardingCodeMethodSchema = z.enum(['SMS', 'VOICE']);

/** What the API returns. The PIN is never included. */
export const whatsappOnboardingSchema = z.object({
  id: z.string().uuid(),
  waba_id: z.string(),
  phone_number: z.string(),
  display_name: z.string(),
  twilio_phone_number_id: z.string().uuid().nullable(),
  code_method: whatsappOnboardingCodeMethodSchema,
  phone_number_id: z.string().nullable(),
  step: whatsappOnboardingStepSchema,
  status: whatsappOnboardingStatusSchema,
  error: z.string().nullable(),
  calling_error: z.string().nullable(),
  code_requested_at: z.date().or(z.string().datetime()).nullable(),
  created_at: z.date().or(z.string().datetime()),
  updated_at: z.date().or(z.string().datetime()),
});
export type WhatsAppOnboarding = z.infer<typeof whatsappOnboardingSchema>;

export const startWhatsappOnboardingSchema = z
  .object({
    waba_id: z.string().min(1, 'Choose a business account'),
    phone_number: z
      .string()
      .regex(/^\+[1-9]\d{7,14}$/, 'Enter the number in international format, e.g. +447700900123'),
    display_name: z.string().trim().min(3, 'Display name is too short').max(255),
    twilio_phone_number_id: z.string().uuid().optional().nullable(),
    code_method: whatsappOnboardingCodeMethodSchema.optional(),
  })
  .refine((v) => !(v.twilio_phone_number_id && v.code_method === 'VOICE'), {
    message: 'Twilio numbers are verified by SMS only',
    path: ['code_method'],
  });
export type StartWhatsAppOnboardingDto = z.infer<typeof startWhatsappOnboardingSchema>;

export const submitWhatsappOnboardingCodeSchema = z.object({
  code: z
    .string()
    .transform((s) => s.replace(/\D/g, ''))
    .pipe(z.string().length(6, 'The code has 6 digits')),
});
export type SubmitWhatsAppOnboardingCodeDto = z.infer<typeof submitWhatsappOnboardingCodeSchema>;
